import { useCallback } from 'react';
import {
  useAnimate3dMutation,
  useAnimationStatusQuery,
  type AnimationLibrary,
  type AnimationStatusResponse,
} from '@/services/avatarApi';

function isTerminalPhase(p?: AnimationStatusResponse['phase']): boolean {
  return p === 'ready' || p === 'failed' || p === undefined;
}

export interface AnimationLifecycle {
  status?: AnimationStatusResponse;
  /** Phase 3: trigger requires a library. force=true regenerates an existing pack.
   *  Optional presetIds narrows the pack to a user-curated subset (Mini App AnimateLibraryModal). */
  trigger: (library: AnimationLibrary, force?: boolean, presetIds?: string[]) => Promise<unknown>;
  isPending: boolean;
}

export function useAnimationLifecycle(
  avatarId: string,
  initialPhase?: AnimationStatusResponse['phase'],
): AnimationLifecycle {
  const [animate, { isLoading: isMutating }] = useAnimate3dMutation();
  const skip = isTerminalPhase(initialPhase) || !avatarId;
  const { data: status } = useAnimationStatusQuery(avatarId, {
    pollingInterval: 4000,
    skip,
  });
  const trigger = useCallback(
    (library: AnimationLibrary, force = false, presetIds?: string[]) =>
      animate({ avatarId, library, force, ...(presetIds ? { presetIds } : {}) }).unwrap(),
    [avatarId, animate],
  );
  return { status, trigger, isPending: isMutating };
}
